import React from "react";
import ZoneSvgPoint from "./ZoneSvgPoint";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import ZoneControllerMouseState from "../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { useSharedSelector } from "../../../store/SharedProvider";
import { selectCurrentTeaching } from "../../../store/room/teaching/teaching-selectors";
import { getSvgCoordinatesFromRosPoint } from "../../../interfaces/robot/rooms/point";
import ZonePointType from "../../../enums/robot/rooms/zone-point-type";
import { selectTeachAndRepeatPath } from "../../../store/room/zone-planner/zone-planner-selectors";
import TeachingWizardStep from "../../../interfaces/robot/wizards/teaching/teaching-wizard-step";
import { selectIsReadOnly } from "../../../store/app-configurations/app-configurations-selectors";

const TeachAndRepeatZonePathSvg: React.FC<{
  zoneId: string;
  roomData: RoomData;
  scale: number;
  mouseState: ZoneControllerMouseState;
}> = ({ zoneId, roomData, scale, mouseState }) => {
  const teachAndRepeatPath = useSharedSelector((state) => selectTeachAndRepeatPath(state));
  const { currentStep: currentTeachingStep } = useSharedSelector((state) => selectCurrentTeaching(state));
  const isReadOnly = useSharedSelector((state) => selectIsReadOnly(state));

  if (!teachAndRepeatPath) {
    return null;
  }

  const svgPoints = teachAndRepeatPath.path
    .slice()
    .map((p) => ({ id: p.id, point: getSvgCoordinatesFromRosPoint(p, roomData) }))
    .filter((p) => p.point != null);

  // Calculate the "mid-points" between each two path points
  let svgMidPoints: { x: number; y: number; insertAtIndex: number }[] = [];
  if (
    svgPoints.length > 1 &&
    (mouseState === ZoneControllerMouseState.Default || mouseState === ZoneControllerMouseState.ShowDetails)
  ) {
    svgMidPoints = svgPoints
      .filter((p, i) => p != null && i < svgPoints.length - 1)
      .map((p, i) => {
        const nextPoint = svgPoints[(i + 1) % svgPoints.length];
        return {
          x: (p.point!.x + nextPoint.point!.x) / 2,
          y: (p.point!.y + nextPoint.point!.y) / 2,
          insertAtIndex: i + 1,
        };
      });
  }

  return (
    <React.Fragment>
      <polyline
        points={svgPoints.map((p) => `${p.point!.x},${p.point!.y}`).join(" ")}
        fill="none"
        stroke="rgb(0,0,150)"
        strokeWidth={0.5}
      />

      {currentTeachingStep !== TeachingWizardStep.TeachingStep && (
        <React.Fragment>
          {(mouseState === ZoneControllerMouseState.Default || mouseState === ZoneControllerMouseState.ShowDetails) &&
            !isReadOnly &&
            svgMidPoints.map((p, i) => (
              <ZoneSvgPoint
                key={i}
                id={null}
                point={p}
                scale={scale}
                type={ZonePointType.TeachAndRepeatZonePathMidPoint}
                enlargingOnHoverEnabled={true}
                midPointInsertAtIndex={p.insertAtIndex}
              />
            ))}
          {svgPoints.map((p) => (
            <ZoneSvgPoint
              key={p.id}
              id={p.id}
              point={p.point!}
              scale={scale}
              draggable={!isReadOnly}
              type={ZonePointType.TeachAndRepeatZonePathPoint}
              enlargingOnHoverEnabled={true}
              midPointInsertAtIndex={null}
            />
          ))}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default TeachAndRepeatZonePathSvg;
